import { useId, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getEditorTextLength, normalizeRichTextStorage } from "../lib/richTextEditor";

/**
 * Rich text field used on job create and response settings (Quill snow, job-create chrome).
 *
 * Must not wrap Quill in `<label>` — the toolbar’s Bold button is the first labelable
 * descendant, so label clicks on the editor area focus Bold instead of the editor.
 */
export default function JobCreateRichTextField({
  label,
  helperText,
  value,
  onChange,
  placeholder,
  maxChars,
  ariaLabel,
  embedded = false,
  onBlur,
  hideCounter = false,
}) {
  const labelId = useId();
  const modules = useMemo(
    () => ({
      toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }, { list: "ordered" }]],
    }),
    []
  );
  const formats = ["bold", "italic", "underline", "list", "bullet"];
  const quillValue = normalizeRichTextStorage(value);
  const currentLength = getEditorTextLength(value);
  const remainingChars = typeof maxChars === "number" ? Math.max(0, maxChars - currentLength) : null;

  const fieldLabel = label || ariaLabel || placeholder || "Rich text";

  return (
    <div
      className={`job-create-rich-text-field${embedded ? " job-create-rich-text-field--embedded" : ""}`}
      role="group"
      aria-label={label && !embedded ? undefined : fieldLabel}
      aria-labelledby={label && !embedded ? labelId : undefined}
    >
      {label && !embedded ? (
        <span id={labelId} className="job-search-label">
          {label}
        </span>
      ) : null}
      {helperText ? <p className="saved-search-meta job-create-helper-text">{helperText}</p> : null}
      <div className="job-create-editor-wrap job-create-quill">
        <ReactQuill
          theme="snow"
          value={quillValue}
          onChange={(html, _delta, _source, editor) => {
            const textLength = editor.getText().trim().length;
            if (typeof maxChars === "number" && textLength > maxChars) return;
            onChange({ target: { value: normalizeRichTextStorage(html) } });
          }}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          onBlur={onBlur}
        />
      </div>
      {typeof maxChars === "number" && !hideCounter ? (
        <span className="saved-search-meta job-create-counter">{remainingChars} characters remaining</span>
      ) : null}
    </div>
  );
}
